/**
 * Link Series: emparejo espacial 2D (nearest neighbor sobre la normal del plano)
 * y fallback por índice relativo. Geometría desde DICOM o metadata de preview.
 */

function asNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function parseVec(raw, expected) {
  if (raw == null) return null
  if (Array.isArray(raw)) {
    const out = raw.map(asNum).filter((n) => n != null)
    return expected && out.length < expected ? null : out
  }
  if (typeof raw === 'string') {
    const out = String(raw)
      .split(/\\|\s+|,/)
      .map(asNum)
      .filter((n) => n != null)
    return expected && out.length < expected ? null : out
  }
  return null
}

function firstString(...vals) {
  for (let i = 0; i < vals.length; i++) {
    if (vals[i] == null || vals[i] === '') continue
    return String(vals[i]).trim()
  }
  return null
}

export function pickGeometryFields(src) {
  if (!src || typeof src !== 'object') return {}
  const ipp = parseVec(
    src.imagePositionPatient || src.ipp || src.ImagePositionPatient,
    3
  )
  const iop = parseVec(
    src.imageOrientationPatient || src.iop || src.ImageOrientationPatient,
    6
  )
  const sliceThickness = asNum(src.sliceThickness || src.SliceThickness)
  const sliceLocation = asNum(src.sliceLocation || src.SliceLocation)
  const pixelSpacing = parseVec(src.pixelSpacing || src.PixelSpacing, 2)
  const frameOfReferenceUID = firstString(
    src.frameOfReferenceUID,
    src.FrameOfReferenceUID,
    src.frameOfReferenceUid
  )
  const rows = asNum(src.rows || src.Rows)
  const columns = asNum(src.columns || src.Columns)
  const out = {}
  if (ipp) out.imagePositionPatient = ipp.slice(0, 3)
  if (iop) out.imageOrientationPatient = iop.slice(0, 6)
  if (sliceThickness != null) out.sliceThickness = sliceThickness
  if (sliceLocation != null) out.sliceLocation = sliceLocation
  if (pixelSpacing) out.pixelSpacing = pixelSpacing.slice(0, 2)
  if (frameOfReferenceUID) out.frameOfReferenceUID = frameOfReferenceUID
  if (rows != null) out.rows = rows
  if (columns != null) out.columns = columns
  return out
}

export function copyGeometryFromManifest(inst, series) {
  return {
    ...pickGeometryFields(series),
    ...pickGeometryFields(inst),
  }
}

export function copyGeometryOnto(target, source) {
  if (!target || !source) return target
  const picked = pickGeometryFields(source)
  Object.keys(picked).forEach((k) => {
    if (target[k] == null && picked[k] != null) target[k] = picked[k]
  })
  return target
}

export function readFileIpp(file) {
  if (!file) return null
  const fromField = parseVec(file.imagePositionPatient || file.ipp, 3)
  if (fromField && fromField.length >= 3) return fromField.slice(0, 3)
  const img = file.image
  if (img && img.data && typeof img.data.string === 'function') {
    try {
      const v = parseVec(img.data.string('x00200032'), 3)
      if (v && v.length >= 3) return v.slice(0, 3)
    } catch (e) {
      /* ignore */
    }
  }
  if (file.sliceLocation != null && Number.isFinite(Number(file.sliceLocation))) {
    return [0, 0, Number(file.sliceLocation)]
  }
  if (file.sliceDistance != null && Number.isFinite(Number(file.sliceDistance))) {
    return [0, 0, Number(file.sliceDistance)]
  }
  return null
}

export function readFileIop(file) {
  if (!file) return null
  const fromField = parseVec(file.imageOrientationPatient || file.iop, 6)
  if (fromField && fromField.length >= 6) return fromField.slice(0, 6)
  const img = file.image
  if (img && img.data && typeof img.data.string === 'function') {
    try {
      const v = parseVec(img.data.string('x00200037'), 6)
      if (v && v.length >= 6) return v.slice(0, 6)
    } catch (e) {
      /* ignore */
    }
  }
  return null
}

export function readFileFoR(file) {
  if (!file) return null
  const fromField = firstString(file.frameOfReferenceUID, file.FrameOfReferenceUID)
  if (fromField) return fromField
  const img = file.image
  if (img && img.data && typeof img.data.string === 'function') {
    try {
      const v = img.data.string('x00200052')
      if (v) return String(v).trim()
    } catch (e) {
      /* ignore */
    }
  }
  return null
}

export function planeNormalFromIop(iop) {
  if (!iop || iop.length < 6) return null
  const ax = iop[0]
  const ay = iop[1]
  const az = iop[2]
  const bx = iop[3]
  const by = iop[4]
  const bz = iop[5]
  const nx = ay * bz - az * by
  const ny = az * bx - ax * bz
  const nz = ax * by - ay * bx
  const len = Math.hypot(nx, ny, nz)
  if (!(len > 1e-8)) return null
  return [nx / len, ny / len, nz / len]
}

export function planeNameFromNormal(n) {
  if (!n) return ''
  const ax = Math.abs(n[0])
  const ay = Math.abs(n[1])
  const az = Math.abs(n[2])
  if (ax >= ay && ax >= az && ax >= 0.8) return 'sagittal'
  if (ay >= ax && ay >= az && ay >= 0.8) return 'coronal'
  if (az >= ax && az >= ay && az >= 0.8) return 'axial'
  return ''
}

export function samePlane(n0, n1, tol = 0.92) {
  if (!n0 || !n1) return false
  const d = Math.abs(n0[0] * n1[0] + n0[1] * n1[1] + n0[2] * n1[2])
  return d >= tol
}

export function projectIpp(ipp, normal) {
  if (!ipp || !normal) return null
  return ipp[0] * normal[0] + ipp[1] * normal[1] + ipp[2] * normal[2]
}

/**
 * Índice del corte cuya proyección sobre `normal` está más cerca de `target`.
 */
export function nearestSliceByNormal(files, target, normal) {
  if (!files || !files.length || target == null || !normal) return -1
  let best = -1
  let bestErr = Infinity
  for (let i = 0; i < files.length; i++) {
    const ipp = readFileIpp(files[i])
    if (!ipp) continue
    const s = projectIpp(ipp, normal)
    const err = Math.abs(s - target)
    if (err < bestErr) {
      bestErr = err
      best = i
    }
  }
  return best
}

export function proportionalSliceIndex(fromIndex, fromMax, toMax) {
  const a = Math.max(1, (fromMax | 0) - 1)
  const b = Math.max(1, (toMax | 0) - 1)
  const t = Math.max(0, Math.min(1, (Number(fromIndex) || 0) / a))
  return Math.max(0, Math.min((toMax | 0) - 1, Math.round(t * b)))
}

export function clampSlice(i, n) {
  if (!(n > 0)) return 0
  return Math.max(0, Math.min(n - 1, i | 0))
}

export function stackGeometry(files) {
  const list = files || []
  let ippCount = 0
  let normal = null
  for (let i = 0; i < list.length; i++) {
    if (readFileIpp(list[i])) ippCount++
    if (!normal) normal = planeNormalFromIop(readFileIop(list[i]))
  }
  return {
    length: list.length,
    hasIpp: ippCount >= 2 || (ippCount === 1 && list.length === 1),
    ippCount,
    normal,
  }
}

function studyUidFromStackFile(file) {
  if (!file || typeof file !== 'object') return ''
  const st = file.study || {}
  return String(
    st.studyId ||
      st.studyInstanceUID ||
      file.studyInstanceUID ||
      file.study_uid ||
      ''
  ).trim()
}

export function readFileInstanceNumber(file) {
  if (!file) return null
  if (file.instanceNumber != null && Number.isFinite(Number(file.instanceNumber))) {
    return Number(file.instanceNumber)
  }
  if (file.InstanceNumber != null && Number.isFinite(Number(file.InstanceNumber))) {
    return Number(file.InstanceNumber)
  }
  return null
}

/** Distancia euclídea entre dos IPP (mm). */
export function ippDistance(a, b) {
  if (!a || !b || a.length < 3 || b.length < 3) return Infinity
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  const dz = a[2] - b[2]
  return Math.hypot(dx, dy, dz)
}

/**
 * Busca en slave el índice con el mismo InstanceNumber que el corte master.
 * @returns {number} índice o -1
 */
export function findSlaveIndexByInstanceNumber(mFiles, mIdx, sFiles) {
  const want = readFileInstanceNumber(mFiles[mIdx])
  if (want == null || !Array.isArray(sFiles)) return -1
  for (let i = 0; i < sFiles.length; i++) {
    const n = readFileInstanceNumber(sFiles[i])
    if (n != null && n === want) return i
  }
  return -1
}

/**
 * Series del mismo estudio / mismo plano / misma longitud con IPP alineados
 * en el índice actual (p. ej. blando vs pulmón): link por índice, no nearest.
 */
export function stacksAreIndexAligned(mFiles, sFiles, mIdx, opts = {}) {
  if (!Array.isArray(mFiles) || !Array.isArray(sFiles)) return false
  if (mFiles.length < 2 || sFiles.length < 2) return false
  if (mFiles.length !== sFiles.length) return false
  const tol = opts.ippTolMm != null ? Number(opts.ippTolMm) : 0.75
  const uidM = studyUidFromStackFile(mFiles[0])
  const uidS = studyUidFromStackFile(sFiles[0])
  if (uidM && uidS && uidM !== uidS) return false
  const geoM = stackGeometry(mFiles)
  const geoS = stackGeometry(sFiles)
  if (
    geoM.normal &&
    geoS.normal &&
    !samePlane(geoM.normal, geoS.normal)
  ) {
    return false
  }
  const idx = clampSlice(mIdx, mFiles.length)
  const ippM = readFileIpp(mFiles[idx])
  const ippS = readFileIpp(sFiles[idx])
  if (ippM && ippS) {
    return ippDistance(ippM, ippS) <= tol
  }
  // Sin IPP en este corte: si InstanceNumber coincide en extremos, asumir alineados
  const a0 = readFileInstanceNumber(mFiles[0])
  const b0 = readFileInstanceNumber(sFiles[0])
  const aN = readFileInstanceNumber(mFiles[mFiles.length - 1])
  const bN = readFileInstanceNumber(sFiles[sFiles.length - 1])
  if (a0 != null && b0 != null && aN != null && bN != null) {
    return a0 === b0 && aN === bN
  }
  // Mismo N + mismo estudio + mismo plano → preferir índice (blando/pulmón)
  return !!(uidM && uidS && uidM === uidS && samePlane(geoM.normal, geoS.normal))
}

/**
 * @returns {{ index: number, mode: 'spatial'|'index'|'index-aligned'|'instance' }}
 */
export function mapMasterSliceToSlave(master, slave, offset = 0) {
  const mFiles = (master && master.files) || []
  const sFiles = (slave && slave.files) || []
  const mIdx = clampSlice(master && master.sliceIndex, mFiles.length)
  const geoM = stackGeometry(mFiles)
  const geoS = stackGeometry(sFiles)
  const mIpp = readFileIpp(mFiles[mIdx])
  const normal = geoS.normal || geoM.normal
  let index = -1
  let mode = 'index'

  // 1) Mismo estudio + mismo N + IPP alineado en i → índice 1:1 (blando/pulmón)
  if (stacksAreIndexAligned(mFiles, sFiles, mIdx)) {
    index = mIdx
    mode = 'index-aligned'
  }

  // 2) Mismo InstanceNumber (si no alineamos por índice)
  if (index < 0) {
    const byIn = findSlaveIndexByInstanceNumber(mFiles, mIdx, sFiles)
    if (byIn >= 0) {
      index = byIn
      mode = 'instance'
    }
  }

  // 3) Nearest IPP (priors / coberturas distintas)
  if (
    index < 0 &&
    geoM.hasIpp &&
    geoS.hasIpp &&
    mIpp &&
    normal &&
    samePlane(geoM.normal || normal, geoS.normal || normal)
  ) {
    const target = projectIpp(mIpp, normal)
    index = nearestSliceByNormal(sFiles, target, normal)
    if (index >= 0) mode = 'spatial'
  }

  // 4) Proporcional
  if (index < 0) {
    index = proportionalSliceIndex(mIdx, mFiles.length, sFiles.length)
    mode = 'index'
  }
  return {
    index: clampSlice(index + (Number(offset) || 0), sFiles.length),
    mode,
  }
}

export function seriesKeyFromViewer(viewer) {
  if (!viewer) return 'v:unknown'
  const files = viewer.files || []
  const f = files[viewer.sliceIndex] || files[0]
  const uid =
    (f && f.seriesInstanceUID) ||
    (f && f.series && f.series.seriesInstanceUID) ||
    (viewer.image && viewer.image.data && typeof viewer.image.data.string === 'function'
      ? viewer.image.data.string('x0020000e')
      : null)
  if (uid) return String(uid)
  if (viewer.explorerIndex != null) return `ex:${viewer.explorerIndex}`
  const idx =
    viewer.layoutIndex != null
      ? viewer.layoutIndex
      : viewer.props && viewer.props.index
  return `v:${idx}`
}

/**
 * Copia IPP/IOP/espesor/FoR desde imagen DICOM o campos de manifest al file.
 */
export function stampSliceGeometry(file, image) {
  if (!file) return file
  const merged = { ...file, image: image || file.image }
  const ipp = readFileIpp(merged)
  const iop = readFileIop(merged)
  const forUid = readFileFoR(merged)
  if (ipp && !file.imagePositionPatient) file.imagePositionPatient = ipp
  if (iop && !file.imageOrientationPatient) file.imageOrientationPatient = iop
  if (forUid && !file.frameOfReferenceUID) file.frameOfReferenceUID = forUid
  const img = image || file.image
  if (img && img.data && typeof img.data.string === 'function') {
    try {
      const th = img.data.string('x00180050')
      if (th != null && file.sliceThickness == null) file.sliceThickness = parseFloat(th)
      const sp = parseVec(img.data.string('x00280030'), 2)
      if (sp && !file.pixelSpacing) file.pixelSpacing = sp
    } catch (e) {
      /* ignore */
    }
  }
  if (img) {
    if (file.rows == null && img.rows != null) file.rows = img.rows
    if (file.columns == null && img.columns != null) file.columns = img.columns
  }
  return file
}
